import axios from "axios";
import elliemaeApi from "./interceptors/elliemaeApi";
import {
    canonicalizeFieldValueMap,
    localizeFieldWritePayload
} from "../utils/loanProgramUtil";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

/**
 * Converts MISMO XML to Loan Object
 * @param {string} accessToken
 * @param {string} xmlContent
 * @returns {Promise<Object|null>} Loan object
 */
export const convertMismoToLoanObject = async (accessToken, xmlContent) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = "/encompass/v3/converter/loans?mediaType=mismo";
    try {
        const response = await elliemaeApi
            .post(url, xmlContent, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/vnd.elliemae.mismo34+xml"
                }
            })
            .then();

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error converting MISMO to Loan Object:", error.message);
        const apiError = error.response?.data?.details || error.message || "Unknown error occurred";
        throw new Error(apiError);
    }
};

/**
 * Fetches TPO Organization details by ID using the new endpoint
 * @param {string} accessToken
 * @param {string} tpoId
 * @returns {Promise<Object|null>} TPO Organization object or null if not found
 */
export const getTpoOrganizationById = async (accessToken, tpoId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!tpoId) {
        console.warn("No TPO ID provided.");
        return null;
    }

    const url = `/encompass/v3/settings/externalOrganizations/tpos?tpoId=${tpoId}`;
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json"
            }
        });

        if (response.status === 200) {
            const data = response.data;

            // If response is an object with summary "Not Found"
            if (!Array.isArray(data) && data.summary === "Not Found") {
                return { notFound: true };
            }

            // If response is an array, return the first matching object
            if (Array.isArray(data)) {
                return data[0] || null;
            }

            return null;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching TPO Organization:", error.message);
        throw error;
    }
};

/**
 * Fetches eFolder document settings
 * @param {string} accessToken
 * @returns {Promise<Array|Object|null>} eFolder document settings payload
 */
export const getEfolderDocumentSettings = async accessToken => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = "/encompass/v3/settings/efolder/documents";
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json"
            }
        });

        if (response.status === 200) {
            return response.data;
        }

        if (response.status === 204) {
            return [];
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error fetching eFolder document settings:", error.message);
        throw error;
    }
};

/**
 * Fetches loan custom field settings and returns options for a specific field id.
 * @param {string} accessToken
 * @param {string} fieldId
 * @returns {Promise<Array<string>>}
 */
export const getLoanCustomFieldOptions = async (accessToken, fieldId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return [];
    }

    if (!fieldId) {
        return [];
    }

    const url = "/encompass/v3/settings/loan/customFields";

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json"
            }
        });

        if (response.status !== 200) {
            throw new Error(`Unexpected status: ${response.status}`);
        }

        const payload = response.data;
        const list = Array.isArray(payload) ? payload : Array.isArray(payload?.customFields) ? payload.customFields : [];
        const match = list.find((entry) => String(entry?.id || "").toUpperCase() === String(fieldId).toUpperCase());
        const options = Array.isArray(match?.options) ? match.options : [];

        return options.map((item) => String(item || "").trim()).filter(Boolean);
    } catch (error) {
        console.error("Error fetching loan custom field settings:", error.message);
        throw error;
    }
};

/**
 * Creates a new Loan in Encompass
 * @param {string} accessToken
 * @param {Object} loanObject
 * @param {string} loanType
 * @returns {Promise<Object|null>} Created Loan Data
 */
export const createLoan = async (
    accessToken,
    loanObject,
    loanType,
    tpoUserId = null,
    channelType = process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE,
    correspondentType = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_DELEGATED
) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    let loanFolder = "";
    let rawTemplatePath = "";
    if (channelType === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT) {
        loanFolder = encodeURIComponent(process.env.REACT_APP_LOAN_FOLDER_CORRESPONDENT || "Corr Pre-Submission");
        if (correspondentType === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_DELEGATED) {
            rawTemplatePath =
                loanType === "consumer" ? process.env.REACT_APP_TEMPLATE_PATH_CORRESPONDENT_DELEGATED : process.env.REACT_APP_TEMPLATE_PATH_CORRESPONDENT_DELEGATED_BUSINESS;
        } else if (correspondentType === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_NON_DELEGATED) {
            rawTemplatePath =
                loanType === "consumer"
                    ? process.env.REACT_APP_TEMPLATE_PATH_CORRESPONDENT_NON_DELEGATED
                    : process.env.REACT_APP_TEMPLATE_PATH_CORRESPONDENT_NON_DELEGATED_BUSINESS;
        }
    } else {
        loanFolder = encodeURIComponent(process.env.REACT_APP_LOAN_FOLDER || "WHL Pre-Submission");
        rawTemplatePath = loanType === "consumer" ? process.env.REACT_APP_TEMPLATE_PATH_CONSUMER : process.env.REACT_APP_TEMPLATE_PATH_BUSINESS;
    }

    const templatePath = encodeURIComponent(rawTemplatePath);

    const url = `/encompass/v3/loans?loanFolder=${loanFolder}&view=entity&templateType=templateSet&templatePath=${templatePath}`;

    // Fetch environment variables
    const consumerconnectid = process.env.REACT_APP_CONSUMER_CONNECT_SITE_ID || "8167064057";
    const enableConsumerSiteId = process.env.REACT_APP_ENABLE_DISABLE_CONSUMER_SITE_ID === "true";

    // Apply Consumer Connect Site ID logic
    if (enableConsumerSiteId) {
        loanObject.consumerConnectSiteId = consumerconnectid;
    }

    try {
        const response = await elliemaeApi.post(url, loanObject, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 201) {
            const createdLoan = response.data;

            // Determine a loanGuid to pass to recentLoans: prefer loanGuid if present, else try id
            const loanGuid = createdLoan.loanGuid || createdLoan.loanId || createdLoan.id || null;

            // If caller provided a TPO user id, try to add the loan to their recentLoans
            if (tpoUserId && loanGuid) {
                try {
                    await addRecentLoan(accessToken, tpoUserId, loanGuid);
                } catch (err) {
                    // don't fail the whole flow if recentLoans fails
                    console.warn("Failed to add recent loan, continuing:", err);
                }
            }

            return createdLoan;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error creating loan:", error.message);
        throw error;
    }
};

/**
 * Get Fields value in a Loan in Encompass
 * @param {string} accessToken
 * @param {Object} fieldsToFetch
 * @param {string} loanid
 * @returns {Promise<Object|null>} Loan Data
 */
export const fieldReader = async (accessToken, fieldsToFetch, loanid) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanid}/fieldReader?invalidFieldBehavior=Include`;
    try {
        const response = await elliemaeApi.post(url, fieldsToFetch, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200) {
            // Loan-product fields carry this Encompass instance's brand name
            // (ACTIVATOR / SUNNY). Translate to canonical codes here so every
            // consumer downstream compares against LOAN_PROGRAM_CODES rather
            // than a company-specific string. See src/utils/loanProgramUtil.js.
            return canonicalizeFieldValueMap(response.data);
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error get loan data:", error.message);
        throw error;
    }
};

/**
 * Write Fields value in a Loan in Encompass
 * @param {string} accessToken
 * @param {string} loanid
 * @param {Object} fieldData
 * @returns {Promise<Object|null>} Updated Loan Data
 */
export const fieldWriter = async (accessToken, loanid, fieldData) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanid}/fieldWriter`;

    // Canonical codes are an application-side vocabulary. Encompass must
    // receive its own brand name, so translate on the way out.
    const localizedFieldData = localizeFieldWritePayload(fieldData);

    try {
        const response = await elliemaeApi.post(url, localizedFieldData, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });
        if (response.status === 204) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error updating loan data:", error.message);
        throw error;
    }
};

export const updateLienHolderFields = async (accessToken, loanId) => {
    const fieldIds = [
        "FL0125",
        "FL0225",
        "FL0325",
        "FL0425",
        "FL0525",
        "FL0625",
        "FL0725",
        "FL0825",
        "FL0925",
        "FL0102",
        "FL0202",
        "FL0302",
        "FL0402",
        "FL0502",
        "FL0602",
        "FL0702",
        "FL0802",
        "FL0902",
        "FM0143",
        "FM0243",
        "FM0343",
        "FM0443",
        "FM0543",
        "FM0643",
        "FM0743"
    ];

    try {
        const fieldValues = await fieldReader(accessToken, fieldIds, loanId);

        const reoFieldIds = ["FM0143", "FM0243", "FM0343", "FM0443", "FM0543", "FM0643", "FM0743"];
        const updatedFields = [];

        for (let i = 0; i < reoFieldIds.length; i++) {
            const reoField = reoFieldIds[i];
            const reoValue = fieldValues[reoField];
            if (!reoValue) continue;

            const matchingNames = [];
            const liabilityFieldMap = {
                FL0125: "FL0102",
                FL0225: "FL0202",
                FL0325: "FL0302",
                FL0425: "FL0402",
                FL0525: "FL0502",
                FL0625: "FL0602",
                FL0725: "FL0702",
                FL0825: "FL0802",
                FL0925: "FL0902"
            };

            for (const [liabilityReoId, lienNameField] of Object.entries(liabilityFieldMap)) {
                if (reoValue === fieldValues[liabilityReoId]) {
                    const name = fieldValues[lienNameField];
                    if (name) matchingNames.push(name);
                }
            }

            if (matchingNames.length > 0) {
                updatedFields.push({
                    id: `CX.BPL.LH.${i + 1}`,
                    value: matchingNames.join(", ")
                });
            }
        }

        if (updatedFields.length > 0) {
            await fieldWriter(accessToken, loanId, updatedFields);
            console.log("Lien holder fields updated:", updatedFields);
        } else {
            console.log("No matching lien holders found.");
        }
    } catch (error) {
        console.error("Error updating lien holders:", error);
    }
};

export const getLienHolderFieldUpdates = async (accessToken, loanId) => {
    const fieldIds = [
        "FL0125",
        "FL0225",
        "FL0325",
        "FL0425",
        "FL0525",
        "FL0625",
        "FL0725",
        "FL0825",
        "FL0925",
        "FL0102",
        "FL0202",
        "FL0302",
        "FL0402",
        "FL0502",
        "FL0602",
        "FL0702",
        "FL0802",
        "FL0902",
        "FM0143",
        "FM0243",
        "FM0343",
        "FM0443",
        "FM0543",
        "FM0643",
        "FM0743"
    ];

    try {
        const fieldValues = await fieldReader(accessToken, fieldIds, loanId);

        const reoFieldIds = ["FM0143", "FM0243", "FM0343", "FM0443", "FM0543", "FM0643", "FM0743"];
        const updatedFields = [];

        for (let i = 0; i < reoFieldIds.length; i++) {
            const reoField = reoFieldIds[i];
            const reoValue = fieldValues[reoField];
            if (!reoValue) continue;

            const matchingNames = [];
            const liabilityFieldMap = {
                FL0125: "FL0102",
                FL0225: "FL0202",
                FL0325: "FL0302",
                FL0425: "FL0402",
                FL0525: "FL0502",
                FL0625: "FL0602",
                FL0725: "FL0702",
                FL0825: "FL0802",
                FL0925: "FL0902"
            };

            for (const [liabilityReoId, lienNameField] of Object.entries(liabilityFieldMap)) {
                if (reoValue === fieldValues[liabilityReoId]) {
                    const name = fieldValues[lienNameField];
                    if (name) matchingNames.push(name);
                }
            }

            if (matchingNames.length > 0) {
                updatedFields.push({
                    id: `CX.BPL.LH.${i + 1}`,
                    value: matchingNames.join(", ")
                });
            }
        }

        return updatedFields;
    } catch (error) {
        console.error("Error collecting lien holder fields:", error);
        return [];
    }
};

export const getConditions = async (accessToken, loanid) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    const url = `/encompass/v3/loans/${loanid}/conditions`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });
        if (response.status === 204 || response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error updating loan data:", error.message);
        throw error;
    }
};

/**
 * Get attachments for a loan in Encompass
 * @param {string} accessToken
 * @param {string} loanid
 * @returns {Promise<Array|null>} Attachments array or null
 */
export const getAttachments = async (accessToken, loanid) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    const url = `/encompass/v3/loans/${loanid}/attachments`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });
        if (response.status === 200) {
            return response.data;
        } else if (response.status === 204) {
            return [];
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching loan attachments:", error.message);
        throw error;
    }
};

/**
 * Get ARM index value from Encompass
 * @param {string} accessToken
 * @param {string} armIndexType
 * @returns {Promise<number|null>}
 */
export const getArmIndex = async (accessToken, armIndexType) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!armIndexType || armIndexType.trim() === "") {
        console.warn("armIndexType is required.");
        return null;
    }

    const url = `/encompass/v1/loans/armIndex?armIndexType=${encodeURIComponent(armIndexType)}`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200) {
            const body = response.data;

            // Try parse indexValue as number or numeric string
            const idxElem = body?.indexValue;
            if (typeof idxElem === "number" && Number.isFinite(idxElem)) {
                return idxElem;
            }
            if (typeof idxElem === "string") {
                const parsed = Number(idxElem);
                if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
                    return parsed;
                }
            }

            console.warn("indexValue missing or invalid:", idxElem, body);
            return null;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error get ARM index:", error.message);
        throw error;
    }
};

/**
 * Get Loan details from Encompass
 * @param {string} accessToken
 * @param {string} loanid
 * @returns {Promise<Object|null>} Loan Data
 */
export const getLoan = async (accessToken, loanid) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!loanid) {
        console.warn("No Loan ID provided.");
        return null;
    }
    const url = `/encompass/v3/loans/${loanid}`;
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json"
            }
        });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching loan details:", error.message);
        throw error;
    }
};

/**
 * Add a loan to a user's recentLoans list in Encompass
 * @param {string} accessToken
 * @param {string|number} tpoUserId  // e.g. 6083889326
 * @param {string} loanGuid         // guid of the loan to add; fallback to loanId if guid missing
 */
export const addRecentLoan = async (accessToken, tpoUserId, loanGuid) => {
    if (!accessToken) {
        console.warn("No Access Token Available for addRecentLoan.");
        return null;
    }
    if (!tpoUserId) {
        console.warn("No tpoUserId provided to addRecentLoan.");
        return null;
    }

    const url = `/encompass/v1/users/${encodeURIComponent(tpoUserId)}/recentLoans?action=add`;

    const payload = JSON.stringify([{ loanGuid }]);

    try {
        const response = await elliemaeApi.request({
            method: "patch",
            url,
            data: payload,
            maxBodyLength: Infinity,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`
            }
        });

        // return response data or status for debugging
        return response.data;
    } catch (err) {
        console.error("addRecentLoan failed:", err?.response?.data || err.message || err);
        // swallow the error so this doesn't stop the main flow, but rethrow if you prefer
        return null;
    }
};

/**
 * Call backend loan controller field-reader endpoint.
 * Body: { AuthCode, LoanId, FieldIds }
 * Returns: mapping fieldId -> value (object)
 *
 * @param {string} authCode - auth code generated via window.elli.script.getObject('auth').createAuthCode()
 * @param {string} loanId
 * @param {string[]} fieldIds
 * @returns {Promise<Object>} fieldId -> value map
 */
export const fieldReaderExternal = async (authCode, loanId, fieldIds) => {
    const url = `${API_DOMAIN}/api/loan/field-reader`;
    try {
        const body = { AuthCode: authCode, LoanId: loanId, FieldIds: fieldIds };
        const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" }
        });

        if (response.status === 200) {
            // expected: JSON object mapping fieldId -> value
            // Loan-product fields arrive as this instance's brand name; hand
            // callers canonical codes, exactly as fieldReader does.
            return canonicalizeFieldValueMap(response.data);
        } else {
            throw new Error(`Unexpected status from field-reader: ${response.status}`);
        }
    } catch (error) {
        console.error("Error calling fieldReaderExternal:", error);
        // rethrow so callers can react (retry, surface toast, etc.)
        throw error;
    }
};

/**
 * Call backend loan controller field-writer endpoint.
 * Body: { AuthCode, LoanId, Fields } where Fields = [{ id, value }, ...]
 * Returns: response (204 No Content expected) — resolves if success, throws if failure.
 *
 * @param {string} authCode - auth code generated via window.elli.script.getObject('auth').createAuthCode()
 * @param {string} loanId
 * @param {{id: string, value: any}[]} fields
 * @returns {Promise<void|Object>} resolves to nothing on 204, or response.data if backend returns body
 */
export const fieldWriterExternal = async (authCode, loanId, fields) => {
    const url = `${API_DOMAIN}/api/loan/field-writer`;
    try {
        // Translate canonical codes back to this instance's brand names, as
        // fieldWriter does.
        const body = { AuthCode: authCode, LoanId: loanId, Fields: localizeFieldWritePayload(fields) };
        const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            validateStatus: null
        });

        // Encompass / your backend typically returns 204 No Content on success
        if (response.status === 204 || (response.status >= 200 && response.status < 300)) {
            return response.data;
        } else {
            // include response body if present for better diagnostics
            const details =
                response.data ||
                (await (async () => {
                    try {
                        return response.statusText;
                    } catch {
                        return "";
                    }
                })());

            const err = new Error(`field-writer failed: ${response.status} ${response.statusText}`);
            err.response = { status: response.status, data: details };
            throw err;
        }
    } catch (error) {
        console.error("Error calling fieldWriterExternal:", error);
        throw error;
    }
};

/**
 * Get Disclosure Tracking 2015 Logs for a loan
 * @param {string} accessToken
 * @param {string} loanId
 * @returns {Promise<Array>} Disclosure Tracking records
 */
export const getDisclosureTrackingLogs = async (accessToken, loanId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return [];
    }

    const url = `/encompass/v3/loans/${loanId}/disclosureTracking2015Logs`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200) {
            return response.data || [];
        } else {
            console.warn(`Unexpected status ${response.status} from disclosureTracking2015Logs`);
            return [];
        }
    } catch (error) {
        console.error("Error fetching disclosureTracking2015Logs:", error.message);
        return [];
    }
};

/**
 * Get Milestone Free Roles of a Loan
 * Extract AE from loanAssociate.role.entityName === "Account Executive"
 *
 * @param {string} accessToken
 * @param {string} loanId
 * @returns {Promise<Array|null>}
 */
export const getMilestoneFreeRoles = async (accessToken, loanId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanId}/milestoneFreeRoles`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200) {
            return response.data;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error fetching milestoneFreeRoles:", error.message);
        throw error;
    }
};

/**
 * Update Milestone Free Roles of a Loan
 * Typically used to set Account Executive role user/entity
 *
 * @param {string} accessToken
 * @param {string} loanId
 * @param {Array} roleDataPayload
 * @returns {Promise<Object|null>}
 */
export const patchMilestoneFreeRoles = async (accessToken, loanId, roleDataPayload) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanId}/milestoneFreeRoles`;

    try {
        const response = await elliemaeApi.patch(url, roleDataPayload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204) {
            return;
        }
    } catch (error) {
        console.error("Error updating milestoneFreeRoles:", error.message);
        throw error;
    }
};

/**
 * Checks if a loan is locked by another user
 * @param {string} token - Bearer Access Token
 * @param {string} loanId - Encompass loan ID
 * @returns {Promise<{ locked: boolean, userId?: string, fullName?: string }>}
 */
export const checkLoanResourceLock = async (token, loanId) => {
    const url = `/encompass/v3/resourceLocks?resourceType=loan&resourceId=${loanId}`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        const locks = response.data || [];

        const exclusive = locks.find(lock => lock.lockType === "Exclusive");

        if (exclusive) {
            return {
                locked: true,
                userId: exclusive.userId,
                fullName: exclusive.fullName
            };
        }

        return { locked: false };
    } catch (error) {
        console.error("Error fetching resource lock:", error);
        return { locked: false };
    }
};

/**
 * Get Ellisign packages for a loan (fallback when tracking logs are empty)
 */
export const getEllisignPackages = async (accessToken, loanId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return [];
    }

    const url = `/ellisign/v3/loans/${loanId}/packages`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        return response.status === 200 ? response.data || [] : [];
    } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;

        if (status === 404 && (data?.code === "EDELIVERY4000" || /Group does not exists/i.test(data?.details || ""))) {
            return []; // treat as "no packages yet"
        }

        console.error("Error fetching ellisign packages:", status, data || error?.message || error);
        return [];
    }
};

/**
 * Returns TRUE if loan already has disclosure evidence:
 * - disclosureTracking2015Logs has at least 1 record OR
 * - ellisign packages has at least 1 package record
 *
 * If tracking logs returns [] (or fails), we fallback to packages.
 */
export const hasDisclosureEvidence = async (accessToken, loanId) => {
    const logs = await getDisclosureTrackingLogs(accessToken, loanId);
    if (Array.isArray(logs) && logs.length > 0) return true;

    const pkgs = await getEllisignPackages(accessToken, loanId);
    return Array.isArray(pkgs) && pkgs.length > 0;
};

export const getLoanApplicationIds = async (accessToken, loanId) => {
    if (!accessToken) return [];

    const url = `/encompass/v3/loans/${loanId}/applications`;

    try {
        const res = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });

        const arr = Array.isArray(res.data) ? res.data : [];
        return arr.map(a => a?.id).filter(Boolean);
    } catch (err) {
        console.error("getLoanApplicationIds error:", err?.response?.status, err?.message || err);
        return [];
    }
};

export const getInitialDisclosureBorrowerApplicationIds = async (accessToken, loanId) => {
    if (!accessToken) return [];

    const url = `/encompass/v3/loans/${loanId}/disclosureTracking2015Logs`;

    try {
        const res = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });

        const logs = Array.isArray(res.data) ? res.data : [];
        const ids = [];

        for (const log of logs) {
            if ((log?.disclosureType || "") !== "Initial") continue;

            const recipients = Array.isArray(log?.disclosureRecipients) ? log.disclosureRecipients : [];
            for (const r of recipients) {
                if ((r?.recipientType || "") !== "Borrower") continue;

                const entityId = r?.role?.application?.entityId;
                if (entityId) ids.push(entityId);
            }
        }

        return Array.from(new Set(ids));
    } catch (err) {
        console.error("getInitialDisclosureBorrowerApplicationIds error:", err?.response?.status, err?.message || err);
        return [];
    }
};

export const hasAllBorrowersDisclosedInitial = async (accessToken, loanId) => {
    const [apps, disclosed] = await Promise.all([getLoanApplicationIds(accessToken, loanId), getInitialDisclosureBorrowerApplicationIds(accessToken, loanId)]);

    if (!apps.length) return false; // no apps => treat as not ready
    if (!disclosed.length) return false;

    const disclosedSet = new Set(disclosed);
    return apps.every(id => disclosedSet.has(id));
};

export async function fetchLoanMilestones(accessToken, loanId) {
    try {
        const response = await elliemaeApi.get(`/encompass/v3/loans/${loanId}/milestones`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        return response.data || [];
    } catch (error) {
        console.error("Milestones fetch failed:", error.response ? error.response.data : error.message);

        throw new Error(`Milestones fetch failed: ${error.response?.status || ""} ${error.response?.statusText || error.message}`);
    }
}

export async function fetchLoanConditions(accessToken, loanId) {
    try {
        const response = await elliemaeApi.get(`/encompass/v3/loans/${loanId}/conditions?view=full`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json"
            }
        });

        return response.data || [];
    } catch (error) {
        console.error("Conditions fetch failed:", error.response ? error.response.data : error.message);

        //throw new Error(`Conditions fetch failed: ${error.response?.status || ""} ${error.response?.statusText || error.message}`);
        throw error;
    }
}

export async function addLoanCondition(accessToken, loanId, conditionPayload) {
    try {
        const response = await elliemaeApi.patch(`/encompass/v3/loans/${loanId}/conditions?action=add&view=entity`, [conditionPayload], {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204) return null;

        return response.data;
    } catch (error) {
        console.error("Create condition failed:", error.response ? error.response.data : error.message);

        //throw new Error(`Create condition failed: ${error.response?.status || ""} ${error.response?.statusText || error.message}`);
        throw error;
    }
}

export async function addConditionTracking(accessToken, loanId, conditionId, trackingPayload) {
    try {
        const response = await elliemaeApi.patch(`/encompass/v3/loans/${loanId}/conditions/${conditionId}/tracking?action=add&view=entity`, [trackingPayload], {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204) return null;

        return response.data;
    } catch (error) {
        console.error("Condition tracking update failed:", error.response ? error.response.data : error.message);

        //throw new Error(`Condition tracking update failed: ${error.response?.status || ""} ${error.response?.statusText || error.message}`);
        throw error;
    }
}

export async function updateLoanMilestone(accessToken, loanId, milestoneId, payload) {
    try {
        const response = await elliemaeApi.patch(`/encompass/v3/loans/${loanId}/milestones/${milestoneId}`, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204) {
            return null;
        }

        return response.data;
    } catch (error) {
        const status = error.response?.status;
        const responseData = error.response?.data;

        console.error("Update milestone failed:", responseData || error.message);

        if ([400, 403, 409].includes(status)) {
            const validationMessages = Array.isArray(responseData?.errors) ? responseData.errors.map(item => item?.details).filter(Boolean) : [];

            if (validationMessages.length === 0 && responseData?.details) {
                validationMessages.push(responseData.details);
            }

            if (validationMessages.length > 0) {
                const validationError = new Error("Milestone validation failed.");

                validationError.code = "MILESTONE_VALIDATION";
                validationError.validationMessages = validationMessages;

                throw validationError;
            }
        }

        throw error;
    }
}

/**
 * Get Late Fee calculation from Encompass
 * @param {string} accessToken
 * @param {string} loanid
 * @returns {Promise<Object|null>} Late Fee Data
 */
export const getLateFee = async (accessToken, loanid) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v1/calculators/lateFees`;

    try {
        const payload = {
            loanId: loanid
        };

        const response = await elliemaeApi.post(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error getting late fee:", error.message);
        throw error;
    }
};

export const addFieldLockData = async (accessToken, loanid, contractPaths) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/loans/${loanid}/fieldLockData?action=add`;

    try {
        const response = await elliemaeApi.patch(url, contractPaths, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204 || response.status === 200) {
            return response.data;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error adding field lock data:", error.message);
        throw error;
    }
};

export const MoveLoanFolderExternal = async (accessToken, loanId, folderName) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `${API_DOMAIN}/api/loan/move-folder`;
    try {
        const body = { AuthCode: accessToken, LoanId: loanId, FolderName: folderName };
        const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            validateStatus: null
        });

        // Encompass / your backend typically returns 204 No Content on success
        if (response.status === 204 || (response.status >= 200 && response.status < 300)) {
            return response.data;
        } else {
            // include response body if present for better diagnostics
            const details =
                response.data ||
                (await (async () => {
                    try {
                        return response.statusText;
                    } catch {
                        return "";
                    }
                })());

            const err = new Error(`move-loan failed: ${response.status} ${response.statusText}`);
            err.response = { status: response.status, data: details };
            throw err;
        }
    } catch (error) {
        console.error("Error calling MoveLoanFolderExternal:", error);
        throw error;
    }
};

export const MoveLoanFolder = async (serviceAccessToken, loanId, folderName, accessToken, comments = "") => {
    if (!serviceAccessToken || !accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const fieldData = [
        {
            id: "1393",
            value: "Application withdrawn"
        },
        {
            id: "CX.LOAN.CANCEL.REASON",
            value: comments,
        }
    ];

    await fieldWriter(accessToken, loanId, fieldData);

    const url = `/encompass/v1/loanfolders/${folderName}/loans?action=add`;
    try {
        var resourceLock = await CheckResourceLock(serviceAccessToken, loanId);
        if (resourceLock && resourceLock.length > 0) {
            var lockDetail = resourceLock[0];
            await UnLockLoanByLockId(serviceAccessToken, lockDetail.id, loanId);
        }

        const body = { loanGuid: loanId };
        const response = await elliemaeApi.patch(url, body, {
            headers: {
                Authorization: `Bearer ${serviceAccessToken}`,
                "Content-Type": "application/json"
            }
        });

        // Encompass / your backend typically returns 204 No Content on success
        if (response.status === 204 || (response.status >= 200 && response.status < 300)) {
            return response.data;
        } else {
            // include response body if present for better diagnostics
            const details =
                response.data ||
                (await (async () => {
                    try {
                        return response.statusText;
                    } catch {
                        return "";
                    }
                })());

            const err = new Error(`move-loan failed: ${response.status} ${response.statusText}`);
            err.response = { status: response.status, data: details };
            throw err;
        }
    } catch (error) {
        console.error("Error calling MoveLoanFolder:", error);
        throw error;
    }
};

const CheckResourceLock = async (accessToken, loanId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }

    const url = `/encompass/v3/resourceLocks?resourceType=loan&resourceId=${loanId}`;
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204 || response.status === 200) {
            return response.data;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error CheckResourceLock:", error.message);
        throw error;
    }
};

const UnLockLoanByLockId = async (accessToken, lockId, loanId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!lockId) {
        console.warn("No LockId Available.");
        return null;
    }

    const url = `/encompass/v3/resourceLocks/${lockId}?resourceType=loan&resourceId=${loanId}`;
    try {
        const response = await elliemaeApi.delete(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (response.status === 204 || response.status === 200) {
            return response.data;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error UnLockLoanByLockId:", error.message);
        throw error;
    }
};
